"use client";

import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { Skeleton } from "@/components/ui";
import { useFetchBlogPosts } from "@/hooks/blog/useFetchBlogPosts";
import type { BlogQueryParams } from "@/lib/blog/filter";
import type { components } from "@/types/api/api-types";
import { BlogCard } from "./BlogCard";

type BlogPost = components["schemas"]["BlogPostCardResponseDto"];

const CardSkeleton = () => (
  <div className="rounded-2xl bg-bg-secondary p-4">
    <Skeleton variant="muted" className="mb-4 aspect-[4/3] w-full rounded-xl" />
    <Skeleton variant="muted" className="h-5 w-3/4 ms-auto" />
    <Skeleton variant="muted" className="mt-2 h-4 w-full" />
    <div className="my-4 border-t border-stroke-light/40" />
    <div className="flex items-center justify-between">
      <Skeleton variant="muted" className="h-4 w-12" />
      <Skeleton variant="muted" className="h-4 w-32" />
    </div>
  </div>
);

interface Props {
  query: BlogQueryParams;
  startPage: number;
  hasMore: boolean;
}

/**
 * Infinite-scroll continuation for /blog. Page 1 is server-rendered HTML; this
 * island fetches page `startPage` (2) onward and appends. When the server says
 * there is no next page (`hasMore=false`) it renders nothing.
 */
export const BlogListMore: React.FC<Props> = ({
  query,
  startPage,
  hasMore,
}) => {
  const posts = useFetchBlogPosts({
    ...query,
    limit: 10,
    startPage,
    enabled: hasMore,
  });
  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && !posts.isFetchingNextPage && posts.hasNextPage) {
      posts.fetchNextPage();
    }
  }, [
    inView,
    posts.isFetchingNextPage,
    posts.hasNextPage,
    posts.fetchNextPage,
  ]);

  if (!hasMore) return null;

  const items: BlogPost[] =
    posts.data?.pages.flatMap((p) => (p?.data?.result ?? []).flat()) ?? [];

  return (
    <>
      {items.map((item) => (
        <BlogCard key={item.id} {...item} />
      ))}
      <div ref={ref} className="py-2">
        {posts.isFetchingNextPage && <CardSkeleton />}
      </div>
    </>
  );
};
